import { RowClassArgs, CommandColumnComponent } from "@progress/kendo-angular-grid";
import { State } from "@progress/kendo-data-query";
export interface IKendoGridData {
    count: number;
    gridData: any;
    columnTotals?: number[];
}

// Define an interface, for which an object implementing the interface can be provided as @Input() 'dataSpy',
// to which this directive class will pass data when it gets loaded by the instance of this.kendoGridService.
export interface IKendoGridDataSpy {
    dataLoaded(loadedData: any[]): void;
}

export class KendoGridHelper {
    // Properties.
    private currentlyLoadedRowCount: number = 0;
    private totalRowCount: number = 0;

    private filter = { logic: 'and', filters: [] };
    private isBeingFiltered = false;

    private contextMenuDataItem: any = null;
    private dataSpy: IKendoGridDataSpy = null;

    // Constructor.
    public constructor() {
    }

    // Setter/getter methods.
    public get Filter() {
        return this.filter;
    }

    /*
        Used to make the "Clear all Filters" button work.
        Add this to you <kendo-grid> config...
        [(filter)]="Helper.Filters"
        Then add something like to the toolbar...
        <ng-container *ngIf="Helper.IsBeingFiltered">
            <button class="clear-all-filters-btn" mat-stroked-button (click)='Helper.handleClearFilters()'>Clear All Filters</button>
            {{Helper.ShowingXofN}}
        </ng-container>
    */
    public set Filter(filter: any) {
        this.isBeingFiltered = filter?.filters?.length > 0;
        this.filter = filter;
    }
    public get IsBeingFiltered() {
        return this.isBeingFiltered;
    }

    public get ContextMenuDataItem(): any {
        return this.contextMenuDataItem;
    }
    public set ContextMenuDataItem(value: any) {
        this.contextMenuDataItem = value;
    }

    public get DataSpy(): IKendoGridDataSpy {
        return this.dataSpy;
    }
    public set DataSpy(value: IKendoGridDataSpy) {
        this.dataSpy = value;
    }

    // Regular methods.
    public handleClearFilters(): void {
        const clearedFilters = this.filter.filters.filter(x => { return x.field == 'FolderId'});
        this.filter = { logic: 'and', filters: clearedFilters };
        this.isBeingFiltered = false;
    }

    // PJH - 1/30/2026 - VNEXT-2206 - with the introduction of Column Menus we now have the possibility that the Kendo Grid will send 
    // nested filters. If that happens this code will "de-nest" them into the single dimension array of filters the server expects
    public unNestAnyNestedFilters(gridState: State) {
        //console.log('Helper.unNestAnyNestedFilters().......................................');
        let flatArrayOfFilters = [];
        if (gridState.filter?.filters && gridState.filter?.filters.length > 0) {
            for (const f of gridState.filter?.filters) {
                // If the filter has nested filters loop them and push them onto flatArrayOfFilters...
                if (f['filters']) {
                    for (let nested of f['filters']) {
                        flatArrayOfFilters.push(nested);
                    }
                    //... otherwise push the already flat filter onto flatArrayOfFilters
                } else {
                    flatArrayOfFilters.push(f);
                }
            }
        }

        //console.log(`REBUILT..................`);
        //console.log(rebuiltFilters);
        gridState.filter.filters = flatArrayOfFilters;
    }

    // sets the value of totalRowCount when it has not yet been initialized
    // and the value of currentlyLoadedRowCount. These values can used to determine
    // if a Kendo Grid has been initialzed, and are also used to render a "Displaying X of N" message
    public handleLoaded(event: IKendoGridData) {
        if (event.gridData?.total > 0) {
            this.totalRowCount = event.gridData.total;
        }
        this.currentlyLoadedRowCount = event.count;

        // 05-02-2024:  added the following two lines of code to accommodate a "data spy".
        if (this.dataSpy != null) {
            let rowData: any[] = event.gridData['data'];
            this.dataSpy.dataLoaded(rowData);
        }            
    }

    public reInit(): void {
        this.currentlyLoadedRowCount = 0;
    }

    public get CurrentlyLoadedRowCount(): number {
        return this.currentlyLoadedRowCount;
    }

    public get TotalRowCount(): number {
        return this.totalRowCount;
    }

    public get TotalRowCountCaption(): string {
        if (this.totalRowCount === 0) return 'No records';
        if (this.totalRowCount === 1) return '1 record';
        return this.totalRowCount + ' records';
    }

    public get ShowingXofN() {
        return `${this.currentlyLoadedRowCount} of ${this.totalRowCount} records`;
    }

    private indexOfClickedRow: number = -1;
    // THIS METHOD SHOULD SUPPLIED to the <kendo-grid [rowClass]> @Input
    // It applies a class called "clickedRow" which you can style as needed (for example, setting white-space: normal !important
    // if you want to turn on wrapping when a row is clicked (See it's use on form-template-instances.component.html)
    public applyClickedRowClassToClickedRow = (context: RowClassArgs) => {
        if (context.index == this.indexOfClickedRow) {
            return { clickedRow: true };
        }
    };

    public setIndexOfClickedRow(event: any) {
        if (event.column instanceof CommandColumnComponent || event.rowIndex < 0) {
            return;
        }
        if (this.indexOfClickedRow == event.rowIndex) {
            this.indexOfClickedRow = -1;
        } else {
            this.indexOfClickedRow = event.rowIndex;
        }
    }
}
